import { FC, useState } from 'react';
import { useGetCompanyMutualFundMarks, useGetCompanyZanbatoReportedMarks } from 'services/hooks/companies';
import { CardBody, CardTitle, CardTitleRow, OutlinedCard } from '../CompanyOverview.styles';
import { EdgarMarksBody } from './EdgarMarksBody';
import { TabButton, TabRow } from './MutualFundMarksCard.styles';
import {
  EndDateModeValue,
  Period,
  PeriodValue,
  ViewMode,
  ViewModeValue,
} from './MutualFundMarksCard.utils';
import { ZanbatoMarksBody } from '../ZanbatoReportedMarksCard';
import {
  DEFAULT_ENABLED_IDS,
  ZanbatoReportedMarksResponse,
} from '../ZanbatoReportedMarksCard/ZanbatoReportedMarksCard.utils';

const MarkSource = { Edgar: 'edgar', Zanbato: 'zanbato' } as const;
type MarkSourceValue = typeof MarkSource[keyof typeof MarkSource];

interface Props {
  companyId: number | undefined;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
}

const MutualFundMarksCard: FC<Props> = ({ companyId, measurementDate, endDateMode }) => {
  const [source, setSource] = useState<MarkSourceValue>(MarkSource.Edgar);

  const [view, setView] = useState<ViewModeValue>(ViewMode.Chart);
  const [edgarPeriod, setEdgarPeriod] = useState<PeriodValue>(Period.All);
  const [zanbatoPeriod, setZanbatoPeriod] = useState<PeriodValue>(Period.All);
  const [selectedFund, setSelectedFund] = useState<string | null>(null);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(DEFAULT_ENABLED_IDS);

  const { data: edgarData, isLoading: edgarLoading } = useGetCompanyMutualFundMarks(companyId);
  const { data: zanbatoData, isLoading: zanbatoLoading } = useGetCompanyZanbatoReportedMarks(companyId);

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <CardTitle>Mutual Fund Marks</CardTitle>
        </CardTitleRow>

        <TabRow>
          <TabButton
            active={source === MarkSource.Edgar}
            onClick={() => setSource(MarkSource.Edgar)}
          >
            EDGAR
          </TabButton>
          <TabButton
            active={source === MarkSource.Zanbato}
            onClick={() => setSource(MarkSource.Zanbato)}
          >
            Zanbato
          </TabButton>
        </TabRow>

        {source === MarkSource.Edgar && (
          <EdgarMarksBody
            data={edgarData}
            isLoading={edgarLoading}
            measurementDate={measurementDate}
            endDateMode={endDateMode}
            view={view}
            onViewChange={setView}
            period={edgarPeriod}
            onPeriodChange={setEdgarPeriod}
            selectedFund={selectedFund}
            onSelectedFundChange={setSelectedFund}
          />
        )}
        {source === MarkSource.Zanbato && (
          <ZanbatoMarksBody
            data={zanbatoData as ZanbatoReportedMarksResponse | undefined}
            isLoading={zanbatoLoading}
            measurementDate={measurementDate}
            endDateMode={endDateMode}
            view={view}
            onViewChange={setView}
            period={zanbatoPeriod}
            onPeriodChange={setZanbatoPeriod}
            enabledIds={enabledIds}
            onEnabledIdsChange={setEnabledIds}
          />
        )}
      </CardBody>
    </OutlinedCard>
  );
};

export { MutualFundMarksCard };
